import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { CoworkerChipsRow, CoworkerGroupChip, CoworkerGroupChipButton, CoworkerNewGroupChip } from './styles';
import { CoworkerGroupResponse } from 'store/api-client/coworker-types';

interface CoworkerGroupChipsProps {
  groups: CoworkerGroupResponse[];
  /** Null means no focus — the "Everyone" chip. */
  activeGroupUuid: string | null;
  onSelect: (groupUuid: string | null) => void;
  onCreate: VoidFunction;
  /**
   * A delete is in flight: every chip is inert meanwhile, so the focus cannot be moved onto a group
   * that is about to disappear (the delete handler decides about the focus when it completes).
   */
  isDeleting: boolean;
}

interface GroupChipProps {
  group: CoworkerGroupResponse;
  isActive: boolean;
  isDeleting: boolean;
  onSelect: (groupUuid: string | null) => void;
}

const GroupChip = ({ group, isActive, isDeleting, onSelect }: GroupChipProps): JSX.Element => {
  // Selecting the active group again clears it, so the row always has a way back to Everyone.
  const handleSelect = useCallback(() => onSelect(isActive ? null : group.uuid), [onSelect, isActive, group.uuid]);

  return (
    <CoworkerGroupChip data-testid={`coworker-group-chip-${group.uuid}`} isActive={isActive}>
      <CoworkerGroupChipButton type="button" aria-pressed={isActive} disabled={isDeleting} onClick={handleSelect}>
        {group.name}
      </CoworkerGroupChipButton>
    </CoworkerGroupChip>
  );
};

/**
 * The saved groups as chips under the search field (§C3): "Everyone", one per group, and "+ New group".
 * The active one is the map's focus mode. Editing and deleting the active group live in the summary's "…" menu.
 */
export const CoworkerGroupChips = ({
  groups,
  activeGroupUuid,
  onSelect,
  onCreate,
  isDeleting,
}: CoworkerGroupChipsProps): JSX.Element => {
  const intl = useIntl();
  const selectEveryone = useCallback(() => onSelect(null), [onSelect]);

  return (
    <CoworkerChipsRow data-testid="coworker-group-chips">
      <CoworkerGroupChip data-testid="coworker-group-chip-everyone" isActive={!activeGroupUuid}>
        <CoworkerGroupChipButton
          type="button"
          aria-pressed={!activeGroupUuid}
          disabled={isDeleting}
          onClick={selectEveryone}
        >
          {intl.formatMessage({ id: 'everyone' })}
        </CoworkerGroupChipButton>
      </CoworkerGroupChip>
      {groups.map(group => (
        <GroupChip
          key={group.uuid}
          group={group}
          isActive={group.uuid === activeGroupUuid}
          isDeleting={isDeleting}
          onSelect={onSelect}
        />
      ))}
      <CoworkerNewGroupChip type="button" data-testid="coworker-group-new" onClick={onCreate}>
        + {intl.formatMessage({ id: 'newGroup' })}
      </CoworkerNewGroupChip>
    </CoworkerChipsRow>
  );
};
